import { ActivatedRoute, createUrlTreeFromSnapshot, Router, RouterOutlet } from '@angular/router';
import { Component, computed, inject, input, OnInit } from '@angular/core';
import { Dialog } from '@shared/components/dialog/dialog';
import { DialogTitle } from '@shared/components/dialog/dialog-title';
import { Button } from '@shared/directives/button/button';
import { Icon } from '@shared/directives/icons/iconify';
import { translateSignal } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import * as AssetDetailSelectors from '@features/asset-detail/store/asset-detail.selectors';
import { Actions, ofType } from '@ngrx/effects';
import { AssetDetailApiActions } from '@features/asset-detail/store/asset-detail.actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgrxStateGuidMgr } from '@core/state/services/ngrx-state-guid-mgr';
import { DropArea } from '@shared/components/documents/drop-area';
import { buildIDocumentUpload } from '@shared/components/documents/utility';
import { FileValidation } from '@shared/components/documents/documents.model';
import { injectUploadDocumentsMutation } from '@core/documents/state/document.commands';
import { LoggerService } from '@core/log/services/logger';
import { injectCanDoOperation } from '@core/role/state/role.selectors';
import { injectInvalidateAllDeviceCountersCache } from '@core/devices/state/device.cache';
import { DocumentComponentState } from '@core/documents/services/document-component-state';
import { OutletLoader } from '@shared/components/loader/outlet-loader';

@UntilDestroy()
@Component({
  selector: 'ta-asset-wrapper',
  template: `
    <ta-dialog
      [closeOnEscape]="!this.windowOpen()"
      [dialogCssClass]="this.dialogCss()"
      [maximize]="!this.outletModal()"
      [showFooter]="false"
      [showHeader]="false"
      [visible]="true"
      (onhide)="this.hideModal()"
    >
      <ng-template #bodyTemplate>
        <div
          class="flex-col-layout relative"
          taDropArea
          [dropAreaDisabled]="this.dropAreaDisabled()"
          (selectedFiles)="this.dropAreaSelectedHandler($event)"
        >
          <div class="flex flex-none pt-4 px-5 pb-6">
            <div class="grow font-normal truncate text-2xl" taDialogTitle [title]="this.headerTitle()">
              {{ this.headerTitle() }}
            </div>
            @if (!this.windowOpen()) {
              <div class="flex-none">
                <button
                  size="icon-sm"
                  taButton
                  type="button"
                  variant="secondary"
                  variantType="plain"
                  [attr.aria-label]="this.closeLabel()"
                  [title]="this.closeLabel()"
                  (click)="this.hideModal()"
                >
                  <span taIcon="times"></span>
                </button>
              </div>
            }
          </div>
          <div class="flex-1 overflow-hidden">
            <router-outlet />
          </div>
          <ta-outlet-loader />
        </div>
      </ng-template>
    </ta-dialog>
  `,
  host: { class: 'contents' },
  providers: [DocumentComponentState],
  imports: [RouterOutlet, Dialog, DialogTitle, Button, Icon, DropArea, OutletLoader]
})
export class AssetDetailWrapper implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly logger = inject(LoggerService);
  private readonly actions$ = inject(Actions);
  private readonly guidService = inject(NgrxStateGuidMgr);
  private readonly invalidateDeviceCounters = injectInvalidateAllDeviceCountersCache();
  private readonly documentComponentState = inject(DocumentComponentState);
  private readonly uploadDocumentByDeviceIdMutation = injectUploadDocumentsMutation();

  protected readonly closeLabel = translateSignal('close');
  protected readonly dropAreaDisabled = computed(() => {
    const asset = this.asset();
    return !asset || !asset.id || asset.id <= 0 || !this.canCreateDocuments();
  });

  // Parametri provenienti dalla rotta
  public readonly outletModal = input(false);
  public readonly windowOpen = input(false);

  public readonly dialogCss = computed(() => (this.outletModal() ? 'h-full 3xl:h-240 w-full lg:w-360' : 'size-full'));
  public canCreateDocuments = injectCanDoOperation('creation', 'Documents');
  public asset = this.store.selectSignal(AssetDetailSelectors.asset(this.guidService.stateId));
  public activeVisibility = this.store.selectSignal(
    AssetDetailSelectors.activeDocumentVisibility(this.guidService.stateId)
  );
  public isLoading = this.store.selectSignal(AssetDetailSelectors.isLoading(this.guidService.stateId));
  public readonly headerTitle = computed(() => {
    const device = this.store.selectSignal(AssetDetailSelectors.asset(this.guidService.stateId))();
    if (!device) {
      return '';
    }
    return device.customerDescr + ' - ' + device.code;
  });

  public ngOnInit(): void {
    this.actions$.pipe(ofType(AssetDetailApiActions.deleteSuccess), untilDestroyed(this)).subscribe(() => {
      this.hideModal();
    });
  }

  public hideModal(): void {
    if (this.outletModal()) {
      const tree = createUrlTreeFromSnapshot(this.activatedRoute.snapshot, []);
      if ('primary' in tree.root.children) {
        this.router.navigate([{ outlets: { modal: null, primary: tree.root.children['primary'].toString() } }], {
          queryParamsHandling: 'merge',
          queryParams: { dId: null, cId: null }
        });
      } else {
        this.logger.error('No primary url available.');
      }
    } else if (this.windowOpen()) {
      window.close();
    }
  }

  protected dropAreaSelectedHandler(e: FileValidation[]): void {
    const entityId = this.asset()?.id;
    if (!entityId || entityId <= 0) {
      return;
    }
    const documents = e
      .filter((d) => d.valid)
      .map((d) => {
        const { documentTypeId, visualizationType } = this.documentComponentState;
        return buildIDocumentUpload(d.file, 'Device', visualizationType(), documentTypeId());
      });

    if (documents.length === 0) {
      return;
    }
    this.uploadDocumentByDeviceIdMutation.mutate(
      { entityId, documents },
      {
        onSuccess: () => {
          this.invalidateDeviceCounters(entityId);
        }
      }
    );
  }
}
